import { useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import moment, { Moment } from 'moment';
import Plotly from 'plotly.js/lib/core';
import { Plot, mainConfig } from '../../common/charts';
import { chartIssuanceSpread, constants } from '../../../constants';
import { chartUtils } from '../../../utils';
import { ChartGrouping, Term } from '../../../types/dashboard/AmrChart';
import { Spread, WeeklyIssuanceSpread } from '../../../types/dashboard/IssuanceSpread';
import { IssuanceSpreadChartFilter } from './IssuanceSpreadFilter';
import { Datum, LayoutAxis, PlotMouseEvent } from 'plotly.js';
import { IssuanceSpreadsChartClickHandler } from './types';
import { TransactionType } from '../../../types/amr-pipeline/enums/TransactionType';
import { FloaterIndex } from '../../../types/amr-pipeline/enums/FloaterIndex';
import { amrChartUtils } from '../../../utils/amr-chart.utils';
import { Nullable } from '../../../types/primitives/Nullable';

interface IssuanceSpreadChartProps {
    issuanceSpread: WeeklyIssuanceSpread[];
    commonMedian: number;
    filter: IssuanceSpreadChartFilter;
    divId?: string;
    onClick?: IssuanceSpreadsChartClickHandler;
}

const MaxRangeYears = 12;

const BarWidthInDays = {
    [ChartGrouping.M]: 15,
    [ChartGrouping.Q]: 45,
    [ChartGrouping.Y]: 182,
    [ChartGrouping.W]: 3,
};

const GroupingTickFormat = {
    [ChartGrouping.M]: {
        tickformat: '%b `%y',
        dtick: 'M1',
    },
    [ChartGrouping.Q]: {
        tickformat: '%qQ `%y',
        dtick: 'M3',
    },
    [ChartGrouping.Y]: {
        tickformat: '%Y',
        dtick: 'M12',
    },
    [ChartGrouping.W]: {
        tickformat: '%m/%d/%y',
        dtick: constants.dayMs * 7,
    },
};

const ChartDimensionFormat = {
    [ChartGrouping.M]: 'YYYY-MM',
    [ChartGrouping.Q]: 'YYYY-MM',
    [ChartGrouping.Y]: 'YYYY',
    [ChartGrouping.W]: 'YYYY-MM-DD',
};

const TooltipDimensionFormat = {
    [ChartGrouping.M]: 'MMM `YY',
    [ChartGrouping.Q]: 'Q[Q] `YY',
    [ChartGrouping.Y]: 'YYYY',
    [ChartGrouping.W]: 'MM/DD/YYYY',
};

export function IssuanceSpreadChart({
    issuanceSpread,
    commonMedian,
    filter,
    divId,
    onClick,
}: IssuanceSpreadChartProps) {
    const {
        chartLayout,
        tickColor,
        tickFont,
        bar,
        border,
        zeroLineColor,
        hoverlabel,
        minAdditionalYAmount,
    } = chartIssuanceSpread;
    const filteredData = useMemo(() => {
        switch (filter.grouping) {
            case ChartGrouping.W:
                return issuanceSpread
                    .filter((x) => !!x.week)
                    .sort(
                        (a, b) =>
                            new Date(a.week as Date).getTime() -
                            new Date(b.week as Date).getTime()
                    );
            case ChartGrouping.M:
                return issuanceSpread
                    .filter((x) => !!x.month)
                    .sort((a, b) => {
                        const yearA = a.year || 0;
                        const yearB = b.year || 0;
                        const monthA = a.month || 0;
                        const monthB = b.month || 0;
                        return yearA - yearB ? yearA - yearB : monthA - monthB;
                    });
            case ChartGrouping.Q:
                return issuanceSpread
                    .filter((x) => !!x.quarter)
                    .sort((a, b) => {
                        const yearA = a.year || 0;
                        const yearB = b.year || 0;
                        const quarterA = a.quarter || 0;
                        const quarterB = b.quarter || 0;
                        return yearA - yearB
                            ? yearA - yearB
                            : quarterA - quarterB;
                    });
            default:
                return issuanceSpread
                    .filter(
                        (x) => !!x.year && !x.month && !x.quarter && !x.week
                    )
                    .sort((a, b) => (a.year as number) - (b.year as number));
        }
    }, [issuanceSpread, filter.grouping]);

    const handleChartClick = (e: PlotMouseEvent) => {
        if (!onClick) {
            return;
        }

        if (!e.points.length) {
            return;
        }

        const [point] = e.points;

        const dimension = point.x;

        const periodStart = moment.utc(dimension);

        const periodEnd = periodStart.clone();

        switch (filter.grouping) {
            case ChartGrouping.M:
                periodEnd.endOf('month');
                break;
            case ChartGrouping.Q:
                periodEnd.endOf('quarter');
                break;
            case ChartGrouping.W:
                periodEnd.endOf('week');
                break;
            case ChartGrouping.Y:
                periodEnd.endOf('year');
                break;
        }

        onClick({
            startPeriod: periodStart.toDate(),
            endPeriod: periodEnd.toDate(),
            transactionType: filter.transactionType === constants.allListItem
                ? undefined
                : filter.transactionType as TransactionType,
            collateralType: filter.collateralType,
            rating: filter.rating,
            currencyCode: filter.currencyCode,
            term: filter.term === constants.allListItem
                ? undefined
                : filter.term as Term,
            floaterIndex: filter.floaterIndex === constants.allListItem
                ? undefined
                : filter.floaterIndex as FloaterIndex,
        });
    };

    const getDimension = (row: WeeklyIssuanceSpread) => {
        switch (filter.grouping) {
            case ChartGrouping.M:
                return row.year && row.month
                    ? moment([row.year, row.month - 1])
                    : undefined;
            case ChartGrouping.W:
                return row.week ? moment(row.week) : undefined;
            case ChartGrouping.Q:
                return row.year && row.quarter
                    ? moment(`${row.year}-${row.quarter}`, 'YYYY-Q', true)
                    : undefined;
            default:
                return row.year ? moment([row.year, 1, 1]) : undefined;
        }
    };

    const getTooltipDimesion = (row: WeeklyIssuanceSpread) =>
        getDimension(row)?.format(TooltipDimensionFormat[filter.grouping]);

    const getChartDimension = (row: WeeklyIssuanceSpread) =>
        getDimension(row)?.format(ChartDimensionFormat[filter.grouping]);

    const getHoverInfoText = (row: WeeklyIssuanceSpread) => {
        const outliers =
            row.bottomOutlier && row.topOutlier
                ? row.bottomOutlier + '; ' + row.topOutlier
                : row.bottomOutlier || row.topOutlier;
        return (
            `${getTooltipDimesion(row)}<br />` +
            `Min: ${row.min} <br />` +
            `Q1: ${row.q1} <br />` +
            `Med: ${row.median} <br />` +
            `Q3: ${row.q3} <br />` +
            `Max: ${row.max} <br />` +
            `Common Med: ${commonMedian} <br />` +
            `Trend Mean: ${row.mean} <br />` +
            (outliers ? `Outlier(s): ${outliers} <br />` : '')
        );
    };

    const getChartRange = (min: Moment, max: Moment) => {
        let rangeMin = min.clone();
        let rangeMax = max.clone();

        switch (filter.grouping) {
            case ChartGrouping.M:
                rangeMin = max.clone()
                    .subtract(11, 'months')
                    .subtract(15, 'days');
                rangeMax.add(15, 'days');
                break;
            case ChartGrouping.W:
                rangeMin = max.clone()
                    .subtract(11, 'weeks')
                    .startOf('week')
                    .subtract(3.5, 'days');
                rangeMax.startOf('week')
                    .add(3.5, 'days');
                break;
            case ChartGrouping.Q:
                rangeMin = max.clone()
                    .subtract(11, 'quarters')
                    .subtract(45, 'days');
                rangeMax.startOf('quarter')
                    .add(45, 'days');
                break;
            default: {
                rangeMin = max.clone()
                    .subtract(MaxRangeYears - 1, 'years')
                    .startOf('year')
                    .subtract(6, 'months');

                if (rangeMin.isBefore(min)) {
                    rangeMin = min.clone()
                        .startOf('year')
                        .subtract(6, 'months');
                }

                rangeMax.startOf('year')
                    .add(6, 'months');
            }
        }

        return [
            rangeMin.format(),
            rangeMax.format(),
        ];
    };

    const getAdditionalYData = (range?: { from: string, to: string }) => {
        const filterSliderRange = (s: WeeklyIssuanceSpread) => {
            if (!range || (!range.from && !range.to)) return true;

            if (filter.grouping === ChartGrouping.M) {
                if (s.month == null || s.year == null) return false;

                const monthFrom = range.from ? moment(range.from) : undefined;
                const monthTo = range.to ? moment(range.to) : undefined;

                return moment({ year: s.year, month: s.month - 1 }).isBetween(monthFrom, monthTo, 'day', "[]");

            } else if (filter.grouping === ChartGrouping.W) {
                if (s.week == null) return false;
                const from = range.from ? moment(range.from) : undefined;
                const to = range.to ? moment(range.to) : undefined;

                return moment(s.week).isBetween(from, to, 'day', "[]");
            } else if (filter.grouping === ChartGrouping.Q) {
                if (s.quarter == null || s.year == null) return false;
                const from = range.from ? moment(range.from) : undefined;
                const to = range.to ? moment(range.to) : undefined;

                return moment(`${s.year}-${s.quarter}`, 'YYYY-Q', true).isBetween(from, to, 'quarter', "[]");
            } else if (filter.grouping === ChartGrouping.Y) {
                if (s.year == null) return false;
                const fromYear = range.from ? moment(range.from).year() + 1 : undefined;
                const toYear = range.to ? moment(range.to).year() + 1 : undefined;

                return (toYear == null || s.year <= toYear) && (fromYear == null || s.year >= fromYear);
            }

            return true;
        }

        const sanitize = (...values: (Nullable<number> | undefined)[]) => values.filter(v => v != null).map(v => Number(v))

        const filteredSpreads = issuanceSpread.filter(filterSliderRange);
        const minValue = Math.min(...filteredSpreads.map(i => Math.min(...sanitize(i.bottomOutlier, i.q1, i.min))));
        const maxValue = Math.max(...filteredSpreads.map(i => Math.max(...sanitize(i.topOutlier, i.q3, i.max))));

        // adding of number with half to prevent empty row on the top and bottom
        const difference = (maxValue - minValue);
        const isLowData = difference < minAdditionalYAmount;

        const rowsToAdd = 1.5;
        const rangeY = [minValue - rowsToAdd, maxValue + rowsToAdd];
        const dtickY = isLowData ? 1 : 0;

        return {
            rangeY,
            dtickY,
        }
    };

    const isSingleValueSpread = (spread: Spread) =>
        spread.min === spread.max &&
        spread.max === spread.median &&
        spread.median === spread.q1 &&
        spread.q1 === spread.q3;


    const getChartData = () => {
        return filteredData.reduce((accum: any, row) => {
            const xAxis = [getChartDimension(row)];
            const isSingleValue = isSingleValueSpread(row);
            const data = [...accum];

            const box = {
                q1: [row.q1],
                q3: [row.q3],
                median: [row.median],
                lowerfence: [row.min],
                upperfence: [row.max],
                x: xAxis,
                name: isSingleValue ? undefined : getHoverInfoText(row),
                type: 'box',
                marker: {
                    color: border,
                },
                width: BarWidthInDays[filter.grouping] * constants.dayMs,
                fillcolor: bar,
                hoverinfo: isSingleValue ? 'skip' : 'name',
                boxpoints: false,
            };

            const topOutlier = {
                x: xAxis,
                y: [row.topOutlier],
                mode: 'markers',
                type: 'scatter',
                hoverinfo: 'skip',
                marker: {
                    size: 6,
                    color: border,
                },
            };

            const bottomOutliers = {
                x: xAxis,
                y: [row.bottomOutlier],
                mode: 'markers',
                type: 'scatter',
                hoverinfo: 'skip',
                marker: {
                    size: 6,
                    color: border,
                },
            };

            data.push(box, topOutlier, bottomOutliers)

            if (isSingleValue) {
                data.push({
                    x: xAxis,
                    y: [row.median],
                    name: getHoverInfoText(row),
                    hoverinfo: 'name',
                    mode: 'none'
                });
            }

            return data;
        }, []);
    };

    const commonMedianData = {
        type: 'scatter',
        x: [0, 100],
        y: filteredData.map(() => commonMedian),
        mode: 'lines',
        xaxis: 'x2',
        hoverinfo: 'skip',
        line: {
            color: 'red',
            width: 2,
            dash: 'dash',
        },
    };

    const trendMeanData = {
        type: 'scatter',
        x: filteredData.map((row) => getChartDimension(row)),
        y: filteredData.map((row) => row.mean),
        mode: 'lines',
        hoverinfo: 'skip',
        line: {
            color: 'green',
            width: 2,
            dash: 'dash',
        },
    };

    const yaxisBase: Partial<LayoutAxis> = {
        showgrid: true,
        showtickprefix: 'all',
        autorange: false,
        fixedrange: false,
        gridcolor: tickColor,
        ticks: 'inside',
        tickcolor: tickColor,
        tickfont: tickFont,
        zerolinecolor: zeroLineColor,
        ticksuffix: ' bps'
    };

    const { min, max } = amrChartUtils.getEdgeDimensionValues(filteredData, getDimension);

    const addGroupingGaps = (from: Moment, to: Moment): [Datum, Datum] => {
        switch (filter.grouping) {
            case ChartGrouping.W: return [
                moment(from).subtract(3, 'day').format(),
                moment(to).add(3, 'day').format()
            ];
            case ChartGrouping.M: return [
                moment(from).subtract(10, 'day').format(),
                moment(to).add(10, 'day').format()
            ];
            case ChartGrouping.Q: return [
                moment(from).subtract(30, 'day').format(),
                moment(to).add(30, 'day').format()
            ];
            default: return [from.format(), to.format()];
        }
    }

    const createXAxis = (from?: string, to?: string): Partial<LayoutAxis> => {
        const { dtick, tickformat } = GroupingTickFormat[filter.grouping];

        return {
            tickcolor: tickColor,
            type: 'date',
            tickfont: tickFont,
            range: [from, to],
            dtick,
            tickformat,
            tick0: min.format(ChartDimensionFormat[filter.grouping]),
            rangeslider: {
                range: addGroupingGaps(min, max),
                bgcolor: 'rgba(79, 123, 156, 0.2)',
            }
        };
    }

    const getLayout = () => {
        const selectedRange = getChartRange(min, max);
        const [from, to] = selectedRange;
        const { dtickY, rangeY } = getAdditionalYData({ from, to });

        return {
            ...chartLayout,
            showlegend: false,
            autosize: true,
            hovermode: 'closest',
            hoverlabel,
            xaxis: createXAxis(from, to),
            yaxis: {
                ...yaxisBase,
                range: rangeY,
                dtick: dtickY,
            },
            xaxis2: {
                zeroline: false,
                showgrid: false,
                fixedrange: true,
                range: [0, 100],
                overlaying: 'x',
                showticklabels: false,
            },
        };
    };

    const data = [...getChartData(), commonMedianData, trendMeanData];
    const layout = getLayout();


    const handleLayoutChange = (e: any) => {
        const [from, to] = e["xaxis.range"] ?? [];
        if (from && to) {
            const { rangeY, dtickY } = getAdditionalYData({ from, to });

            Plotly.relayout(divId!, {
                yaxis: { ...yaxisBase, dtick: dtickY, autorange: false, range: rangeY },
            });
        }
    };

    // eslint-disable-next-line
    const debouncedRelayoutChange = useCallback(debounce(handleLayoutChange, 500), []);

    return (
        <>
            <Plot
                onHover={chartUtils.setCursor('pointer')}
                onUnhover={chartUtils.setCursor('crosshair')}
                onRelayout={debouncedRelayoutChange}
                onClick={handleChartClick}
                divId={divId}
                data={data}
                layout={layout}
                config={mainConfig}
            />
            <div className="agenda">
                <div className="common-median">Common Median</div>
                <div className="trend-mean">Trend Mean</div>
                <div className="outlier">Outlier(s)</div>
            </div>
        </>
    );
}

IssuanceSpreadChart.defaultProps = {
    divId: 'issuance-spread-chart',
};
